import type { GridPaginationModel } from '@mui/x-data-grid';
import { useCallback, useMemo, useState } from 'react';

/**
 * Cursor → page pagination adapter. Introduced for the users list
 * (AUT-124 substep 1); promoted to a shared hook in AUT-123 so the orders
 * list reuses it verbatim — the bridge is fully generic over the list
 * surface (no users-specific assumption).
 *
 * The admin cursor list endpoints (`GET /admin/users`, `GET /admin/orders`)
 * are CURSOR-based (`{cursor, limit}` → `{items, nextCursor}`, fixed
 * `createdAt DESC`, NO total count). The `AdminDataGrid` (AUT-122) is
 * PAGE-based (`paginationModel {page, pageSize}` + `rowCount`). This hook
 * bridges the two.
 *
 * Strategy — a client-side **cursor stack**:
 *   - `stack[0]` is always `null` (page 0 = no cursor).
 *   - Advancing to the next page pushes the server's `nextCursor`.
 *   - Going back pops to the previous cursor.
 *   - Changing `pageSize` resets the stack (the cursor space is invalid
 *     once the slice width changes).
 *
 * Forward-only paging (next / prev) is the v1 UX decision — there is no
 * jump-to-arbitrary-page because a cursor for page N can only be learned
 * by walking pages 0..N-1. The DataGrid still renders prev/next via the
 * MUI X unknown-total sentinel (`rowCount = -1`) when more pages exist.
 *
 * Usage:
 *   const pager = useCursorPagination();
 *   const { data } = useAdminUsersList({ ...filters, cursor: pager.cursor,
 *     limit: pager.pageSize });
 *   pager.onResult(data?.nextCursor ?? null);  // after each fetch
 *   <AdminDataGrid
 *     paginationModel={pager.paginationModel}
 *     onPaginationModelChange={pager.onPaginationModelChange}
 *     rowCount={pager.rowCount(data?.items.length ?? 0)} />
 */

/** Default page size — first of the DataGrid options [25, 50, 100]. */
export const DEFAULT_PAGE_SIZE = 25;

/** MUI X DataGrid sentinel for "unknown total" — renders prev/next only. */
const UNKNOWN_ROW_COUNT = -1;

export interface UseCursorPaginationResult {
  /** Cursor to send to the backend for the current page (`null` = first). */
  cursor: string | null;
  /** Current page size (forwarded to the backend `limit`). */
  pageSize: number;
  /** Zero-based page index, derived from the cursor-stack depth. */
  page: number;
  /** DataGrid-shaped model (page + pageSize). */
  paginationModel: GridPaginationModel;
  /**
   * Record the latest fetch's `nextCursor` so a forward step knows where
   * to go. Call once per successful list fetch. `null` means "last page".
   */
  onResult: (nextCursor: string | null) => void;
  /** Bridge the DataGrid model change (page delta / pageSize change). */
  onPaginationModelChange: (model: GridPaginationModel) => void;
  /**
   * Compute the `rowCount` to feed the grid. When a further page exists
   * (`nextCursor` recorded) we return the MUI X unknown-total sentinel so
   * the pager keeps "next" live; otherwise the exact count of rows seen
   * so far (so the pager disables "next" on the final page).
   */
  rowCount: (currentPageItems: number) => number;
  /** Reset to the first page (e.g. when filters change). */
  reset: () => void;
}

export const useCursorPagination = (
  initialPageSize: number = DEFAULT_PAGE_SIZE,
): UseCursorPaginationResult => {
  // The stack of cursors for pages 0..page. `stack[page]` is the cursor
  // that fetched the current page (index 0 is always `null`).
  const [stack, setStack] = useState<Array<string | null>>([null]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  // The `nextCursor` the server returned for the current page (or null).
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const cursor = stack[page] ?? null;

  const onResult = useCallback((next: string | null) => {
    setNextCursor(next);
  }, []);

  const reset = useCallback(() => {
    setStack([null]);
    setPage(0);
    setNextCursor(null);
  }, []);

  const onPaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      // Page-size change invalidates the entire cursor space → reset.
      if (model.pageSize !== pageSize) {
        setPageSize(model.pageSize);
        setStack([null]);
        setPage(0);
        setNextCursor(null);
        return;
      }

      if (model.page === page) return;

      if (model.page > page) {
        // Forward: only valid if the server handed us a next cursor.
        if (nextCursor === null) return;
        setStack((prev) => {
          const copy = prev.slice(0, page + 1);
          copy[page + 1] = nextCursor;
          return copy;
        });
        setPage(page + 1);
        // The next page's nextCursor is unknown until its fetch settles.
        setNextCursor(null);
      } else {
        // Backward: pop to the previous page (forward-only beyond -1 step).
        setPage(Math.max(0, model.page));
        // We don't know the popped page's nextCursor; the refetch resets
        // it. Conservatively assume a further page exists (we came from it).
        setNextCursor(stack[page] ?? null);
      }
    },
    [nextCursor, page, pageSize, stack],
  );

  const paginationModel = useMemo<GridPaginationModel>(
    () => ({ page, pageSize }),
    [page, pageSize],
  );

  const rowCount = useCallback(
    (currentPageItems: number): number => {
      if (nextCursor !== null) return UNKNOWN_ROW_COUNT;
      // Final page: report the exact running total so "next" disables.
      return page * pageSize + currentPageItems;
    },
    [nextCursor, page, pageSize],
  );

  return {
    cursor,
    pageSize,
    page,
    paginationModel,
    onResult,
    onPaginationModelChange,
    rowCount,
    reset,
  };
};
